import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Terminal, 
  ShieldCheck, 
  Database, 
  Brain, 
  Code, 
  ArrowLeft, 
  Copy, 
  Check, 
  Lock, 
  Server,
  Cpu,
  RefreshCw,
  Clock,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";

const IntegrationDocs = () => {
  const [activeSection, setActiveSection] = useState("overview");

  const sections = [
    { id: "overview", title: "Visión General", icon: Brain },
    { id: "auth", title: "Autenticación & Seguridad", icon: Lock },
    { id: "inbound", title: "Inbound (Webhooks)", icon: RefreshCw },
    { id: "agents", title: "Lógica de Agentes", icon: Cpu },
    { id: "outbound", title: "Outbound (CRM Mapping)", icon: Database },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Header */}
      <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-gray-900 transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Brain className="h-5 w-5 text-white" />
              </div>
              <span className="font-black text-slate-900 tracking-tight">SarAI <span className="text-blue-600">Docs</span></span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest border border-gray-100 px-2 py-0.5 rounded">v1.2.0</span>
            <Link to="/dashboard" className="text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors">
              Ir al Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto flex">
        {/* Sidebar */}
        <aside className="w-64 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto border-r border-gray-50 p-6 hidden md:block">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-bold transition-all",
                  activeSection === section.id 
                    ? "bg-blue-50 text-blue-600" 
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                )}
              >
                <section.icon className="h-4 w-4" />
                {section.title}
              </button>
            ))}
          </nav>

          <div className="mt-12 p-4 rounded-xl bg-slate-50 border border-slate-100">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Soporte Técnico</h4>
            <div className="text-xs font-bold text-slate-700 mb-1">api-support@sarai.com</div>
            <p className="text-[10px] text-slate-500">Tiempo de respuesta: &lt; 2 horas</p>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 px-6 md:px-12 py-12 max-w-4xl">
          {activeSection === "overview" && <OverviewSection />}
          {activeSection === "auth" && <AuthSection />}
          {activeSection === "inbound" && <InboundSection />}
          {activeSection === "agents" && <AgentSection />}
          {activeSection === "outbound" && <OutboundSection />}
        </main>
      </div>
    </div>
  );
};

const OverviewSection = () => (
  <div className="animate-fade-in">
    <h1 className="text-4xl font-black text-slate-900 mb-6">Integración Técnica SarAI</h1>
    <p className="text-lg text-slate-600 mb-8 leading-relaxed">
      Esta guía detalla el proceso para conectar SarAI con tu ecosistema corporativo. Nuestra arquitectura está diseñada 
      para ser agnóstica al CRM, permitiendo integraciones nativas con Salesforce, HubSpot, Zoho y Dynamics 365 a través de un canal seguro de alta disponibilidad.
    </p>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
      <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
        <Cpu className="h-6 w-6 text-blue-600 mb-4" />
        <h3 className="font-bold text-slate-900 mb-2">Motor de Decisión</h3>
        <p className="text-sm text-slate-500">Clasificación automática en &lt; 500ms mediante el Agente Maestro.</p>
      </div>
      <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
        <Server className="h-6 w-6 text-emerald-600 mb-4" />
        <h3 className="font-bold text-slate-900 mb-2">Escalabilidad</h3>
        <p className="text-sm text-slate-500">Soporta hasta 10,000 PQRSD concurrentes sin pérdida de fidelidad.</p>
      </div>
    </div>

    <h2 className="text-2xl font-black text-slate-900 mb-4 underline decoration-blue-500 decoration-4 underline-offset-8">Flujo Lógico de Alto Nivel</h2>
    <div className="mt-8 space-y-4">
      {[
        "Captura en CRM Externo",
        "Disparo de Webhook Inbound",
        "Procesamiento Neuronal (SarAI)",
        "Sincronización a través de Webhook Outbound",
        "Actualización de Ticket y Notificación"
      ].map((step, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-black text-xs">{i + 1}</div>
          <span className="font-bold text-slate-700">{step}</span>
        </div>
      ))}
    </div>
  </div>
);

const AuthSection = () => (
  <div className="animate-fade-in">
    <h2 className="text-3xl font-black text-slate-900 mb-6">Autenticación & Seguridad</h2>
    <p className="text-slate-600 mb-8">
      Todas las solicitudes a la API de SarAI requieren una firma digital válida y deben realizarse a través de TLS 1.3.
    </p>

    <div className="mb-8">
      <h3 className="text-xl font-bold mb-4">API Keys</h3>
      <p className="text-sm text-slate-500 mb-4">Debes incluir el token en el Header de cada petición:</p>
      <CodeBlock code={`Authorization: Bearer SARAI_API_KEY_LIVE_...`} />
    </div>

    <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-start gap-4">
      <ShieldCheck className="h-6 w-6 text-emerald-600 shrink-0" />
      <div>
        <h4 className="font-bold text-emerald-900 mb-1">Cifrado de Datos (E2EE)</h4>
        <p className="text-xs text-emerald-800 leading-relaxed">
          Los datos en tránsito se cifran con TLS 1.3. Los datos en reposo se almacenan utilizando AES-256 con gestión de claves rotativas en HSM (Hardware Security Module).
        </p>
      </div>
    </div>
  </div>
);

const InboundSection = () => (
  <div className="animate-fade-in">
    <h2 className="text-3xl font-black text-slate-900 mb-6">Inbound Webhook Specification</h2>
    <p className="text-slate-600 mb-8">
      Utiliza este schema para enviar datos de PQRSD desde tu CRM a SarAI.
    </p>

    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold text-slate-400">Endpoint URL (Production)</span>
        <Badge text="POST" color="bg-blue-600" />
      </div>
      <CodeBlock code={`https://api.sarai-legal.com/v1/ingest/pqrsd`} />
    </div>

    <h3 className="font-bold text-slate-900 mb-4">Request Body (JSON)</h3>
    <CodeBlock code={`{
  "source_id": "CRM_SF_123456",
  "customer_id": "CUST_098",
  "type": "RECLAMO",
  "subject": "Solicitud de baja de servicio no atendida",
  "content": "Buenas tardes, he solicitado la cancelación de mi plan el día 05/04 y aún recibo cargos...",
  "metadata": {
    "channel": "WHATSAPP",
    "region": "BOGOTA",
    "priority": "HIGH"
  }
}`} />

    <div className="mt-8 overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] font-black">
          <tr>
            <th className="px-4 py-2">Campo</th>
            <th className="px-4 py-2">Tipo</th>
            <th className="px-4 py-2">Descripción</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <tr><td className="px-4 py-3 font-mono font-bold">source_id</td><td className="px-4 py-3">string</td><td className="px-4 py-3">ID único del ticket en tu CRM.</td></tr>
          <tr><td className="px-4 py-3 font-mono font-bold">content</td><td className="px-4 py-3">string</td><td className="px-4 py-3">Cuerpo del mensaje a analizar.</td></tr>
          <tr><td className="px-4 py-3 font-mono font-bold">type</td><td className="px-4 py-3">enum</td><td className="px-4 py-3">PETICION, QUEJA, RECLAMO.</td></tr>
        </tbody>
      </table>
    </div>
  </div>
);

const AgentSection = () => (
  <div className="animate-fade-in">
    <h2 className="text-3xl font-black text-slate-900 mb-6">Lógica de Clasificación</h2>
    <p className="text-slate-600 mb-8">
      SarAI utiliza un sistema de agentes jerárquicos. El Agente Maestro analiza el <code>content</code> e identifica la competencia legal.
    </p>

    <div className="space-y-6">
      {[
        { 
          agent: "Derechos de Petición", 
          logic: "Si detecta invocación de Artículo 23 o términos legales de urgencia.",
          response_time: "< 15s"
        },
        { 
          agent: "Hábeas Data", 
          logic: "Si el contenido refiere a eliminación de datos o Ley 1581.",
          response_time: "< 10s"
        },
        { 
          agent: "PQRSD General", 
          logic: "Casos comerciales, reclamos de servicio o quejas estándar.",
          response_time: "< 30s"
        }
      ].map((item, i) => (
        <div key={i} className="p-4 rounded-xl border border-gray-100 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-slate-900">{item.agent}</h4>
            <p className="text-xs text-slate-500">{item.logic}</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-black text-blue-600 uppercase">SLA IA</div>
            <div className="font-mono text-sm font-bold">{item.response_time}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const OutboundSection = () => (
  <div className="animate-fade-in">
    <h2 className="text-3xl font-black text-slate-900 mb-6">Outbound Sync (Callback)</h2>
    <p className="text-slate-600 mb-8">
      Una vez generada la respuesta, SarAI enviará un POST a tu URL configurada con el siguiente payload:
    </p>

    <CodeBlock code={`{
  "request_id": "SARAI_UUID_9988",
  "original_id": "CRM_SF_123456",
  "status": "COMPLETED",
  "intelligence": {
    "generated_response": "Estimado Cliente, en respuesta a su solicitud...",
    "tags": ["RECLAMO", "RIESGO_MEDIO"],
    "legal_metadata": {
      "deadline": "2026-04-15",
      "risk_score": 0.45,
      "root_cause": "RETRASO_LOGISTICO"
    }
  }
}`} />

    <div className="mt-12 p-8 rounded-3xl bg-slate-900 text-white">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Database className="h-5 w-5 text-blue-400" />
        Sugerencia de Mapeo (Salesforce)
      </h3>
      <ul className="space-y-3 text-sm text-slate-300">
        <li className="flex justify-between border-b border-white/5 pb-2">
          <span>legal_metadata.deadline</span>
          <span className="font-mono text-blue-400">DueDate__c</span>
        </li>
        <li className="flex justify-between border-b border-white/5 pb-2">
          <span>intelligence.generated_response</span>
          <span className="font-mono text-blue-400">AI_Draft_Reply__c</span>
        </li>
        <li className="flex justify-between pb-2">
          <span>legal_metadata.risk_score</span>
          <span className="font-mono text-blue-400">Risk_Level__c</span>
        </li>
      </ul>
    </div>
  </div>
);

const CodeBlock = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="p-4 rounded-xl bg-slate-50 border border-slate-100 text-slate-800 font-mono text-xs overflow-x-auto">
        <code>{code}</code>
      </pre>
      <button 
        onClick={copy}
        className="absolute top-3 right-3 p-2 rounded-lg bg-white shadow-sm border border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3 text-slate-400" />}
      </button>
    </div>
  );
};

const Badge = ({ text, color = "bg-slate-900" }: { text: string; color?: string }) => (
  <span className={cn("px-2 py-0.5 rounded text-[10px] font-black text-white uppercase tracking-widest", color)}>
    {text}
  </span>
);

export default IntegrationDocs;
